import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Loaders from "components/Common/Loaders";
import { statusType } from "helpers/GeneralHelper";
import { getExternalLeadById } from "model/ExternalLeadsModel";

const formatRawPayloadForDisplay = (rawPayload) => {
  if (rawPayload == null || rawPayload === "") return "";
  if (typeof rawPayload === "object") {
    return JSON.stringify(rawPayload, null, 2);
  }
  const trimmed = String(rawPayload).trim();
  if (!trimmed) return "";
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return trimmed;
  }
};

const ExternalLeadViewModal = ({ isOpen = false, toggle, leadId = "" }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setDetail(null);
      return;
    }

    const normalizedLeadId = String(leadId ?? "").trim();
    if (!normalizedLeadId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    const loadDetail = async () => {
      setLoading(true);
      try {
        const response = await getExternalLeadById(normalizedLeadId);
        if (cancelled) return;
        const data = response?.data ?? response ?? {};
        setDetail(data && typeof data === "object" ? data : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [isOpen, leadId]);

  const candidateName = String(detail?.candidateName ?? "").trim();
  const clientName = String(detail?.clientName ?? "").trim();
  const status = String(detail?.status ?? "").trim();
  const responseMessage = String(detail?.responseMessage ?? "").trim();
  const statusClassName = status
    ? statusType(String(status).trim().toLowerCase())
    : "";
  const rawPayloadDisplay = useMemo(
    () => formatRawPayloadForDisplay(detail?.rawPayload),
    [detail?.rawPayload]
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="modal-full-height">
      <ModalHeader toggle={toggle}>External Lead</ModalHeader>
      <ModalBody>
        {loading ? (
          <Loaders />
        ) : (
          <>
            {candidateName ? (
              <div className="mb-2">
                <span className="fw-semibold">Candidate: </span>
                <span>{candidateName}</span>
              </div>
            ) : null}
            {clientName ? (
              <div className="mb-2">
                <span className="fw-semibold">Client: </span>
                <span>{clientName}</span>
              </div>
            ) : null}
            {status ? (
              <div className="mb-2">
                <span className="fw-semibold">Status: </span>
                <span
                  className={`px-2 py-1 fw-semibold common-table-status-badge ${statusClassName}`}
                >
                  {status}
                </span>
              </div>
            ) : null}
            {responseMessage ? (
              <div className="mb-3">
                <span className="fw-semibold">Response Message: </span>
                <span>{responseMessage}</span>
              </div>
            ) : null}
            <div className="fw-semibold mb-2">Raw Payload</div>
            <pre
              className="mb-0 p-3 rounded border bg-light small"
              style={{ maxHeight: "70vh", overflow: "auto", whiteSpace: "pre-wrap" }}
            >
              {rawPayloadDisplay || "No payload available."}
            </pre>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

ExternalLeadViewModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ExternalLeadViewModal;
